/**
 * Nearby — browser-geolocated map + list of stores and shops within a radius.
 *
 * The backend does the proximity search (2dsphere $geoNear via nearbyStores /
 * nearbyShops): we fetch once at the widest radius option and get back only the
 * in-range places, sorted nearest-first, each carrying a server-computed
 * `distanceKm`. The radius pills then narrow that set client-side (no refetch).
 * `parseLatLng` is still used to place map markers from the "lat,lng" string.
 *
 * On top of that base set the page layers a stats strip (area overview), a
 * name/address search, a "deals only" quick filter, and a sort control — all
 * client-side over data already in memory, so they're instant.
 *
 * This is also the ONLY screen that asks for the user's precise location, and
 * it asks behind the in-context disclosure below (`LocationDisclosure`) rather
 * than straight away — see that component for why.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BadgePercent,
  Compass,
  MapPin,
  MapPinOff,
  Navigation,
  Search,
  ShoppingBag,
  Store as StoreIcon,
  UserRound,
  X,
  type LucideIcon,
} from 'lucide-react';
// Leaflet touches `window` at import: the map loads lazily, in the browser only
// (web plan W4.2). The type import is erased at build time.
import { ClientLeafletMap } from '@/components/map/ClientLeafletMap';
import type { MapMarker } from '@/components/map/LeafletMap';
import { Button, CardSkeleton, EmptyState, Img, SectionHeader, Skeleton, Spinner } from '@/components/ui';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { PromotionCard } from '@/components/cards';
import { useFeaturedPromotions, useNearbyShops, useNearbyStores } from '@/hooks/useAppData';
import { useNavCities } from '@/hooks/useNavCities';
import { useRouteLocale } from '@/hooks/useRouteLocale';
import { PageHead } from '@/components/seo/PageHead';
import { sitePath } from '@/seo/hubLinks';
import { NEARBY_LIVE_DEALS_LIMIT } from '@/seo/pageHeads';
import { nearbyHead } from '@/seo/templates';
import { displayName } from '@/seo/text';
import { entityPath } from '@/seo/urls';
import { useAuth } from '@/context/AuthContext';
import { persistGeoLocation } from '@/lib/geoCapture';
import { trackClick } from '@/services/tracking';
import { parseLatLng, formatDistance, type LatLng } from '@/utils/geo';
import { safeImageUrl, ImagePresets } from '@/utils/safeImageUrl';
import type { Media } from '@/types';

const RADIUS_OPTIONS = [10, 30, 50] as const;

/**
 * Top featured promotions shown above the geolocation prompt (W3.5).
 *
 * Googlebot never grants geolocation, so the page must surface live content
 * before the location flow. Four cards fit a standard 4-column grid without
 * widening the bundle (PromotionCard is already in the SPA chunk). The SEO
 * server reads the same page for the route's server copy (web plan W4.5).
 */
const LIVE_DEALS_LIMIT = NEARBY_LIVE_DEALS_LIMIT;
type RadiusKm = (typeof RADIUS_OPTIONS)[number];

type TypeFilter = 'all' | 'stores' | 'shops';

const TYPE_OPTIONS: Array<{ key: TypeFilter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'stores', label: 'Stores' },
  { key: 'shops', label: 'Shops' },
];

type SortKey = 'distance' | 'promotions' | 'name';

const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: 'distance', label: 'Nearest' },
  { key: 'promotions', label: 'Most deals' },
  { key: 'name', label: 'Name A–Z' },
];

// 'consent' = the disclosure is on screen and nothing has been requested yet.
type LocationStatus = 'consent' | 'locating' | 'denied' | 'unavailable' | 'ready';

interface NearbyPlace {
  id: string;
  kind: 'store' | 'shop';
  /** Its page: `urlPath` when the data has it, else the id path (`entityPath`, web plan W0.16). */
  path: string;
  name: string;
  latLng: LatLng;
  distanceKm: number;
  logo?: Media;
  address?: string;
  promotionsCount: number;
}

// ─── Filter pill ─────────────────────────────────────────────────────────────

interface FilterPillProps {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}

function FilterPill({ active, onClick, children }: FilterPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all duration-200',
        active
          ? 'bg-primary text-white'
          : 'border border-line bg-surface text-ink-secondary hover:border-primary/40 hover:text-ink',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

// ─── Stat tile ───────────────────────────────────────────────────────────────

interface StatTileProps {
  icon: LucideIcon;
  label: string;
  value: number | string;
}

function StatTile({ icon: Icon, label, value }: StatTileProps) {
  return (
    <div className="flex items-center gap-2.5 rounded-2xl border border-line bg-surface px-3.5 py-2.5">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon size={17} aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <p className="text-lg font-extrabold leading-none text-ink">{value}</p>
        <p className="mt-0.5 truncate text-xs font-medium text-ink-secondary">{label}</p>
      </div>
    </div>
  );
}

// ─── List row ────────────────────────────────────────────────────────────────

interface PlaceRowProps {
  place: NearbyPlace;
  /** Fired before the link navigates (analytics). */
  onClick: () => void;
}

/**
 * A real link, not a button that calls navigate(): it can be opened in a new
 * tab, shows its target on hover, and is announced as a link.
 */
function PlaceRow({ place, onClick }: PlaceRowProps) {
  const meta = [
    place.kind === 'store' ? 'Store' : 'Shop',
    place.address || null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Link
      to={place.path}
      onClick={onClick}
      className="flex w-full items-center gap-3 p-3 text-left transition-all duration-200 hover:bg-line-light"
    >
      {/* Decorative: the name is right next to it, so alt text would make the
          link announce the name twice. */}
      <Img
        media={place.logo ?? null}
        transform={ImagePresets.thumb}
        alt=""
        fit="contain"
        className="h-[46px] w-[46px] shrink-0 rounded-xl border border-line-light"
        fallback={<StoreIcon size={20} className="text-ink-tertiary" aria-hidden="true" />}
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-ink">{place.name}</p>
        <p className="line-clamp-1 text-xs text-ink-secondary">{meta}</p>
        {place.promotionsCount > 0 && (
          <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-bold text-primary">
            <BadgePercent size={11} aria-hidden="true" />
            {place.promotionsCount} deal{place.promotionsCount === 1 ? '' : 's'}
          </span>
        )}
      </div>
      <span className="flex shrink-0 items-center gap-1 self-start rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
        <Navigation size={12} aria-hidden="true" />
        {formatDistance(place.distanceKm)}
      </span>
    </Link>
  );
}

function PlaceRowSkeleton() {
  return (
    <div className="flex w-full items-center gap-3 p-3">
      <Skeleton className="h-[46px] w-[46px] shrink-0 rounded-xl" />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-3.5 w-2/5 rounded" />
        <Skeleton className="h-3 w-3/5 rounded" />
      </div>
      <Skeleton className="h-6 w-16 shrink-0 rounded-full" />
    </div>
  );
}

// ─── Location disclosure ─────────────────────────────────────────────────────

/**
 * Remembers that the user has already answered the disclosure, so agreeing once
 * doesn't mean re-reading it on every visit. Stored the way the app stores its
 * other local preferences — a plain namespaced localStorage key (see
 * lib/webPush.ts, context/ThemeContext.tsx); there is no shared wrapper.
 */
const LOCATION_CONSENT_KEY = 'promostork.web.locationConsent';

type LocationConsent = 'granted' | 'declined';

function readLocationConsent(): LocationConsent | null {
  try {
    const raw = window.localStorage.getItem(LOCATION_CONSENT_KEY);
    return raw === 'granted' || raw === 'declined' ? raw : null;
  } catch {
    // Storage blocked (private mode / third-party cookie rules) — we simply ask
    // again. Never treat an unreadable store as agreement.
    return null;
  }
}

function writeLocationConsent(value: LocationConsent): void {
  try {
    window.localStorage.setItem(LOCATION_CONSENT_KEY, value);
  } catch {
    /* non-fatal: the disclosure just shows again next visit */
  }
}

interface LocationDisclosureProps {
  onAccept: () => void;
  onDecline: () => void;
}

/**
 * Shown BEFORE the browser's location prompt.
 *
 * Opening "Nearby" explains only ONE of the two things we do with the fix: it
 * makes searching around you obvious, but nobody taps Nearby expecting their
 * coordinates to be filed on their account for staff to look at. Store policy
 * (and plain fairness) wants the disclosure up front, naming the data AND every
 * purpose — so both uses are spelled out here and NOTHING is requested until
 * the user picks "Use my location".
 *
 * Note there is deliberately no close button and no backdrop to click away:
 * ignoring or dismissing this must never be able to count as agreement. The
 * only way forward is the affirmative button.
 */
function LocationDisclosure({ onAccept, onDecline }: LocationDisclosureProps) {
  return (
    <div className="mx-auto my-10 max-w-md animate-fade-up rounded-3xl border border-line bg-surface p-6 shadow-sm sm:p-8">
      <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
        <MapPin size={28} className="text-primary" aria-hidden="true" />
      </div>
      <h2 className="text-center text-xl font-extrabold tracking-tight text-ink">
        Use your location?
      </h2>
      <p className="mt-1 text-center text-sm text-ink-secondary">
        Before your browser asks, here is exactly what we do with it.
      </p>

      <ul className="mt-6 space-y-4">
        <li className="flex gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Navigation size={17} aria-hidden="true" />
          </span>
          <p className="text-sm text-ink-secondary">
            <span className="font-bold text-ink">Find stores and shops near you.</span>{' '}
            Your coordinates go to our servers to search the area around you.
          </p>
        </li>
        <li className="flex gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <UserRound size={17} aria-hidden="true" />
          </span>
          <p className="text-sm text-ink-secondary">
            <span className="font-bold text-ink">Save it to your account.</span> If you are
            signed in, your latest location is kept on your account, and our team can see
            it on an internal map of where our users are.
          </p>
        </li>
      </ul>

      <p className="mt-5 text-xs text-ink-tertiary">
        We only use your location while you are on this screen, never in the background.
        You can say no — the rest of PromoStork works either way.
      </p>

      <div className="mt-6 space-y-2">
        <Button
          className="w-full"
          leftIcon={<MapPin size={18} aria-hidden="true" />}
          onClick={onAccept}
        >
          Use my location
        </Button>
        <Button variant="ghost" className="w-full" onClick={onDecline}>
          Not now
        </Button>
      </div>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function NearbyPage() {
  // Title, description and H1 from the formulas (web plan W0.6).
  const locale = useRouteLocale();
  const head = nearbyHead({ locale });
  const navigate = useNavigate();
  const { user } = useAuth();

  // City hubs and live deals rendered WITHOUT geolocation so crawlers
  // (Googlebot never grants location) see real, indexable content (W3.5).
  const navCities = useNavCities();
  const liveDeals = useFeaturedPromotions(LIVE_DEALS_LIMIT);

  const [status, setStatus] = useState<LocationStatus>('locating');
  // 'denied' is reached two ways — the browser refused, or the user answered
  // "Not now" on our own disclosure. Only the copy differs; telling someone who
  // never got a browser prompt to go fix their browser settings is nonsense.
  const [declined, setDeclined] = useState(false);
  const [position, setPosition] = useState<LatLng | null>(null);
  const [radiusKm, setRadiusKm] = useState<RadiusKm>(10);
  /**
   * The radius actually asked of the server — the widest pill used so far.
   *
   * The pills used to be the query variable outright, so every change round-
   * tripped, including narrowing back to a radius already on screen (50 km ->
   * 10 km refetched a set we were holding a superset of). Widening still has to
   * fetch, but narrowing never does: a smaller radius is a subset of a bigger
   * one, and `places` already drops anything past the selected radius.
   */
  const [fetchRadiusKm, setFetchRadiusKm] = useState<RadiusKm>(10);
  const selectRadius = useCallback((next: RadiusKm) => {
    setRadiusKm(next);
    setFetchRadiusKm((current) => (next > current ? next : current));
  }, []);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('distance');
  const [dealsOnly, setDealsOnly] = useState(false);

  // Fetch at the SELECTED radius — the server does the filtering (2dsphere
  // $geoNear), returning only in-range places sorted nearest-first with
  // distanceKm.
  //
  // This used to fetch once at the widest pill (50 km) and narrow locally,
  // which meant every visit pulled the whole 50 km set no matter which pill was
  // active — 773 stores around Lahore — and the paging loop silently stopped
  // short of that. Asking for the radius the user actually picked makes the
  // common case (10 km) far smaller and the widest case complete.
  const stores = useNearbyStores(position, fetchRadiusKm);
  const shops = useNearbyShops(position, fetchRadiusKm);
  const dataLoading = stores.loading || shops.loading;

  // Tracks the in-flight geolocation attempt so a new request (Try again) or an
  // unmount cancels any pending retry.
  const geoAttemptRef = useRef<{ cancelled: boolean; timer: ReturnType<typeof setTimeout> | null }>({
    cancelled: false,
    timer: null,
  });

  const requestLocation = useCallback(() => {
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setStatus('unavailable');
      return;
    }

    // Cancel any previous in-flight attempt/retry, then start a fresh one.
    geoAttemptRef.current.cancelled = true;
    if (geoAttemptRef.current.timer) clearTimeout(geoAttemptRef.current.timer);
    const token: { cancelled: boolean; timer: ReturnType<typeof setTimeout> | null } = {
      cancelled: false,
      timer: null,
    };
    geoAttemptRef.current = token;

    setStatus('locating');
    setDeclined(false);

    // On desktop (esp. macOS/Chrome) the first fix often fails with
    // POSITION_UNAVAILABLE — CoreLocation reports `kCLErrorLocationUnknown`
    // while it's still acquiring a Wi-Fi fix. That error is TRANSIENT, so we
    // retry a few times before giving up instead of failing on the first miss.
    // Permission denial, by contrast, is terminal.
    const attempt = (retriesLeft: number): void => {
      if (token.cancelled) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (token.cancelled) return;
          setPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setStatus('ready');
          // Persist this precise fix for logged-in users (admin "Users by
          // location"). Best-effort; guests are skipped (mutation needs auth).
          if (user) persistGeoLocation(`${pos.coords.latitude},${pos.coords.longitude}`);
        },
        (err) => {
          if (token.cancelled) return;
          if (err.code === err.PERMISSION_DENIED) {
            setStatus('denied');
            return;
          }
          // POSITION_UNAVAILABLE (kCLErrorLocationUnknown) / TIMEOUT — retry.
          if (retriesLeft > 0) {
            token.timer = setTimeout(() => attempt(retriesLeft - 1), 1200);
            return;
          }
          setStatus('unavailable');
        },
        // Coarse (Wi-Fi/IP) is faster and more reliable than GPS on desktop.
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
      );
    };
    attempt(3);
  }, [user]);

  /**
   * The gate in front of `requestLocation`: nothing may touch
   * `navigator.geolocation` except through here, because `requestLocation`
   * itself assumes the user has already read the disclosure and agreed.
   *
   * The ONLY thing that lets us skip the disclosure is OUR OWN record of the
   * user having tapped "Use my location". Deliberately NOT the browser's
   * permission state: a granted permission means the prompt won't fire, but the
   * prompt was never the point. What has to be disclosed first is the
   * COLLECTION and, above all, the second purpose — that the fix is filed on
   * the account for an internal staff map — and the browser tells the user
   * nothing about that. Anyone carrying a permission granted under the old
   * login-screen flow would otherwise have their precise location taken and
   * saved here having never once been told why; that is exactly the finding
   * this change exists to fix. Same reason a stored "Not now" can't be
   * overridden by a granted permission.
   *
   * `honourStoredDecline` is true only for the automatic run on mount: someone
   * who said no shouldn't be re-asked just for opening the screen. Tapping
   * "Try again" is them asking, so it always brings the disclosure back — never
   * a silent request.
   */
  const startLocationFlow = useCallback(
    (honourStoredDecline: boolean) => {
      if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
        setStatus('unavailable');
        return;
      }

      const consent = readLocationConsent();
      if (consent === 'granted') {
        requestLocation();
        return;
      }
      if (consent === 'declined' && honourStoredDecline) {
        setDeclined(true);
        setStatus('denied');
        return;
      }
      setDeclined(false);
      setStatus('consent');
    },
    [requestLocation],
  );

  // What the retry buttons call: re-show the disclosure rather than re-request.
  const beginLocation = useCallback(() => startLocationFlow(false), [startLocationFlow]);

  const acceptDisclosure = useCallback(() => {
    writeLocationConsent('granted');
    requestLocation();
  }, [requestLocation]);

  const declineDisclosure = useCallback(() => {
    writeLocationConsent('declined');
    // Exactly the dead-end the page already had when the browser refuses: no
    // map and no list here, everything else on the site untouched. Nothing is
    // requested and nothing is saved.
    setDeclined(true);
    setStatus('denied');
  }, []);

  useEffect(() => {
    startLocationFlow(true);
    return () => {
      geoAttemptRef.current.cancelled = true;
      if (geoAttemptRef.current.timer) clearTimeout(geoAttemptRef.current.timer);
    };
  }, [startLocationFlow]);

  // Base in-radius set (type filter). The server filters to `fetchRadiusKm`;
  // the `distanceKm > radiusKm` guards below narrow that to the pill the user
  // has selected, which is what makes narrowing free.
  // Nearest-first — stats and the default sort read off this order.
  const places: NearbyPlace[] = useMemo(() => {
    if (!position) return [];
    const out: NearbyPlace[] = [];
    if (typeFilter !== 'shops') {
      for (const store of stores.items) {
        const distanceKm = store.distanceKm;
        if (typeof distanceKm !== 'number' || distanceKm > radiusKm) continue;
        const latLng = parseLatLng(store.location);
        if (!latLng) continue;
        out.push({
          id: store.id,
          kind: 'store',
          path: entityPath(store, 'store'),
          // Cleaned (𝗯𝗼𝗹𝗱 letters, emoji, hashtags out): the row, the pin's
          // tooltip, the name search and the A–Z sort all read this.
          name: displayName(store.name),
          latLng,
          distanceKm,
          logo: store.logo,
          address: store.address,
          // W3.8: backend-filtered promotions (running + upcoming, store's own first).
          promotionsCount: store.activePromotions?.length || 0,
        });
      }
    }
    if (typeFilter !== 'stores') {
      for (const shop of shops.items) {
        const distanceKm = shop.distanceKm;
        if (typeof distanceKm !== 'number' || distanceKm > radiusKm) continue;
        const latLng = parseLatLng(shop.location);
        if (!latLng) continue;
        out.push({
          id: shop.id,
          kind: 'shop',
          path: entityPath(shop, 'shop'),
          name: displayName(shop.name),
          latLng,
          distanceKm,
          logo: shop.logo,
          address: shop.address,
          promotionsCount: shop.promotions?.length ?? 0,
        });
      }
    }
    return out.sort((a, b) => a.distanceKm - b.distanceKm);
  }, [position, stores.items, shops.items, radiusKm, typeFilter]);

  // Area overview — stays stable while the user searches/filters below it.
  const stats = useMemo(() => {
    const storeCount = places.reduce((n, p) => n + (p.kind === 'store' ? 1 : 0), 0);
    const withDeals = places.reduce((n, p) => n + (p.promotionsCount > 0 ? 1 : 0), 0);
    return {
      total: places.length,
      storeCount,
      shopCount: places.length - storeCount,
      withDeals,
      nearest: places.length ? places[0].distanceKm : null,
    };
  }, [places]);

  // The rendered subset: search + deals filter + sort. `places` is already
  // nearest-first, so 'distance' needs no re-sort (filters preserve order).
  const visiblePlaces = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = places;
    if (dealsOnly) list = list.filter((p) => p.promotionsCount > 0);
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) || (p.address ?? '').toLowerCase().includes(q),
      );
    }
    if (sortBy === 'promotions') {
      list = [...list].sort(
        (a, b) => b.promotionsCount - a.promotionsCount || a.distanceKm - b.distanceKm,
      );
    } else if (sortBy === 'name') {
      list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [places, search, dealsOnly, sortBy]);

  const hasActiveRefinements = search.trim() !== '' || dealsOnly;
  const clearRefinements = useCallback(() => {
    setSearch('');
    setDealsOnly(false);
  }, []);

  const markers: MapMarker[] = useMemo(
    () =>
      visiblePlaces.map((place) => ({
        id: place.id,
        kind: place.kind,
        lat: place.latLng.lat,
        lng: place.latLng.lng,
        title: place.name,
        logoUrl: safeImageUrl(place.logo ?? null, ImagePresets.logo),
      })),
    [visiblePlaces],
  );

  // Map pins are Leaflet markers, not DOM links, so they still navigate from a
  // handler (to the same path as the place's row); the list rows below are
  // real links.
  const openPlace = useCallback(
    (id: string, kind: 'store' | 'shop') => {
      trackClick(kind, id);
      const place = places.find((p) => p.id === id && p.kind === kind);
      navigate(place?.path ?? entityPath({ id }, kind));
    },
    [navigate, places],
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <PageHead head={head} />
      {/* Home › Sales Near You: the BreadcrumbList's own array (web plan W0.8). */}
      <Breadcrumbs crumbs={head.breadcrumbs} className="mb-4" />
      <h1 className="text-2xl font-extrabold tracking-tight text-ink">{head.h1}</h1>
      <p className="mt-1 text-sm text-ink-secondary">Stores and shops around you</p>

      {/* ─── City picker ─────────────────────────────────────────────────────
          Rendered unconditionally — Googlebot never grants geolocation so
          crawlers must see real content. Each link goes to an indexable city
          hub (/pk/en/city/:slug). Only cities where seo.indexable is true
          are shown (useNavCities already filters these). (W3.5)          */}
      {(navCities.loading || navCities.items.length > 0) && (
        <section aria-label="Deals by city" className="mt-8">
          <SectionHeader title="Deals by city" />
          {navCities.loading ? (
            <div className="mt-3 flex flex-wrap gap-2" aria-hidden="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-24 rounded-full" />
              ))}
            </div>
          ) : (
            <ul className="mt-3 flex flex-wrap gap-2">
              {navCities.items.map((city) => {
                const to = sitePath(city.urlPath);
                if (!to) return null;
                return (
                  <li key={city.slug}>
                    <Link
                      to={to}
                      className="inline-flex items-center gap-1.5 rounded-full border border-line bg-surface px-3.5 py-1.5 text-sm font-semibold text-ink-secondary transition-colors hover:border-primary/40 hover:text-primary"
                    >
                      <MapPin size={13} aria-hidden="true" />
                      {city.name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* ─── Live deals ──────────────────────────────────────────────────────
          Top featured promotions shown before the geolocation prompt so the
          page has useful content for Googlebot and for users who prefer not
          to share their location. Same query as the home page rail. (W3.5) */}
      {(liveDeals.loading || liveDeals.items.length > 0) && (
        <section aria-label="Live deals" className="mt-8">
          <SectionHeader title="Live deals" />
          <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {liveDeals.loading && liveDeals.items.length === 0
              ? Array.from({ length: LIVE_DEALS_LIMIT }).map((_, i) => <CardSkeleton key={i} />)
              : liveDeals.items.map((promo) => (
                  <PromotionCard key={promo.id} promotion={promo} variant="grid" />
                ))}
          </div>
        </section>
      )}

      {status === 'consent' && (
        <LocationDisclosure onAccept={acceptDisclosure} onDecline={declineDisclosure} />
      )}

      {status === 'locating' && (
        <div className="flex flex-col items-center justify-center gap-3 py-24">
          <Spinner size={32} className="text-primary" />
          <p className="text-sm text-ink-secondary">Finding your location…</p>
        </div>
      )}

      {status === 'denied' && (
        <EmptyState
          icon={MapPinOff}
          title={declined ? 'Location not shared' : 'Location access needed'}
          message={
            declined
              ? 'You chose not to share your location, so there is nothing to show here. The rest of PromoStork works as usual, and you can change your mind any time.'
              : 'Allow location access in your browser to find stores and shops around you.'
          }
          action={
            <Button onClick={beginLocation}>{declined ? 'Use my location' : 'Try again'}</Button>
          }
        />
      )}

      {status === 'unavailable' && (
        <EmptyState
          icon={MapPinOff}
          title="Location unavailable"
          message="We couldn't determine your location. Check that location services are enabled and try again."
          action={<Button onClick={beginLocation}>Try again</Button>}
        />
      )}

      {status === 'ready' && position && (
        <div className="mt-6">
          {/* Stats strip — area overview at the current radius/type */}
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            <StatTile icon={MapPin} label={`Within ${radiusKm} km`} value={stats.total} />
            <StatTile icon={StoreIcon} label="Stores" value={stats.storeCount} />
            <StatTile icon={ShoppingBag} label="Shops" value={stats.shopCount} />
            <StatTile
              icon={BadgePercent}
              label="With deals"
              value={stats.withDeals}
            />
          </div>

          {/* Radius + type filters */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {RADIUS_OPTIONS.map((r) => (
              <FilterPill key={r} active={radiusKm === r} onClick={() => selectRadius(r)}>
                {r} km
              </FilterPill>
            ))}
            <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" />
            {TYPE_OPTIONS.map((t) => (
              <FilterPill
                key={t.key}
                active={typeFilter === t.key}
                onClick={() => setTypeFilter(t.key)}
              >
                {t.label}
              </FilterPill>
            ))}
          </div>

          {/* Map + list — split view on lg, stacked on mobile */}
          <div className="lg:grid lg:grid-cols-[1fr_420px] lg:gap-6">
            <ClientLeafletMap
              markers={markers}
              userLocation={position}
              radiusKm={radiusKm}
              onMarkerClick={openPlace}
              className="h-72 lg:h-[560px]"
            />

            <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-surface lg:mt-0">
              {/* Search + sort + deals filter */}
              <div className="space-y-2.5 border-b border-line p-3">
                <div className="relative">
                  <Search
                    size={16}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary"
                    aria-hidden="true"
                  />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name or address…"
                    aria-label="Search nearby places"
                    className="w-full rounded-xl border border-line bg-background py-2 pl-9 pr-9 text-sm text-ink placeholder:text-ink-tertiary focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  {search && (
                    <button
                      type="button"
                      onClick={() => setSearch('')}
                      aria-label="Clear search"
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-full p-1 text-ink-tertiary transition-colors hover:bg-line-light hover:text-ink"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                  <h2 className="text-sm font-bold text-ink">
                    {visiblePlaces.length} place{visiblePlaces.length === 1 ? '' : 's'}
                  </h2>
                  {stats.nearest != null && (
                    <span className="text-xs text-ink-secondary">
                      · nearest {formatDistance(stats.nearest)}
                    </span>
                  )}
                  {dataLoading && <Spinner size={14} className="shrink-0 text-primary" />}

                  <div className="ml-auto flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setDealsOnly((v) => !v)}
                      aria-pressed={dealsOnly}
                      className={[
                        'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200',
                        dealsOnly
                          ? 'bg-primary text-white'
                          : 'border border-line bg-surface text-ink-secondary hover:border-primary/40 hover:text-ink',
                      ].join(' ')}
                    >
                      <BadgePercent size={13} aria-hidden="true" />
                      Deals
                    </button>
                    <label htmlFor="nearby-sort" className="sr-only">
                      Sort places
                    </label>
                    <select
                      id="nearby-sort"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as SortKey)}
                      className="rounded-full border border-line bg-surface px-3 py-1.5 text-xs font-semibold text-ink-secondary focus:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                    >
                      {SORT_OPTIONS.map((o) => (
                        <option key={o.key} value={o.key}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {visiblePlaces.length > 0 ? (
                <ul className="max-h-[468px] divide-y divide-line-light overflow-y-auto">
                  {visiblePlaces.map((place) => (
                    <li key={`${place.kind}-${place.id}`}>
                      <PlaceRow
                        place={place}
                        onClick={() => trackClick(place.kind, place.id)}
                      />
                    </li>
                  ))}
                </ul>
              ) : dataLoading ? (
                <div className="divide-y divide-line-light">
                  <PlaceRowSkeleton />
                  <PlaceRowSkeleton />
                  <PlaceRowSkeleton />
                  <PlaceRowSkeleton />
                </div>
              ) : places.length > 0 ? (
                <EmptyState
                  icon={Search}
                  title="No matches"
                  message="No nearby places match your search or filters."
                  action={
                    hasActiveRefinements ? (
                      <Button variant="outline" onClick={clearRefinements}>
                        Clear filters
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <EmptyState
                  icon={Compass}
                  title="Nothing nearby yet"
                  message={`No registered stores or shops within ${radiusKm} km. Try a wider radius.`}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
